import { FC, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Checkbox, Modal, ModalProps } from "@vilocnv/allsetra-core";

export type ColumnPickerColumn = {
  id: string;
  name: React.ReactNode;
};

export type ColumnPickerProps = Pick<ModalProps, "open" | "onClose"> & {
  columns: ColumnPickerColumn[];
  hiddenIds: string[];
  protectedIds: string[];
  onApply: (hiddenIds: string[]) => void;
  title?: string;
};

const ColumnPicker: FC<ColumnPickerProps> = ({
  open,
  onClose,
  columns,
  hiddenIds,
  protectedIds,
  onApply,
  title = "Columns"
}) => {
  const theme = useTheme();
  const protectedSet = useMemo(() => new Set(protectedIds), [protectedIds]);
  const [draftHidden, setDraftHidden] = useState<string[]>(hiddenIds);

  useEffect(() => {
    if (open) {
      setDraftHidden(hiddenIds);
    }
  }, [open, hiddenIds]);

  const visibleCount = columns.length - draftHidden.length;

  const isChecked = (id: string) => !draftHidden.includes(id);

  const toggleColumn = (id: string, checked: boolean) => {
    if (protectedSet.has(id)) return;

    setDraftHidden((prev) => {
      if (checked) {
        return prev.filter((hiddenId) => hiddenId !== id);
      }
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
  };

  const handleReset = () => {
    setDraftHidden([]);
  };

  const handleApply = () => {
    onApply(draftHidden);
    onClose();
  };

  const handleCancel = () => {
    setDraftHidden(hiddenIds);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={title}
      primaryBtnProps={{
        text: "Apply",
        onClick: handleApply
      }}
      secondaryBtnProps={{
        text: "Cancel",
        onClick: handleCancel
      }}
      theme={theme}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {visibleCount} of {columns.length} columns visible
        </Typography>

        <Stack spacing={1}>
          {columns.map((column) => {
            const protectedColumn = protectedSet.has(column.id);
            return (
              <Box key={column.id}>
                <Checkbox
                  name={`column-${column.id}`}
                  label={
                    protectedColumn
                      ? `${String(column.name)} (always visible)`
                      : String(column.name)
                  }
                  value={isChecked(column.id)}
                  disabled={protectedColumn}
                  onChange={(event) => {
                    // allsetra-core Checkbox passes the MUI change event, not a boolean
                    const checked =
                      typeof event === "boolean"
                        ? event
                        : Boolean(event?.target?.checked);
                    toggleColumn(column.id, checked);
                  }}
                  theme={theme}
                />
              </Box>
            );
          })}
        </Stack>

        <Box>
          <Typography
            component="button"
            type="button"
            onClick={handleReset}
            sx={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: theme.palette.primary.main,
              textDecoration: "underline",
              font: "inherit"
            }}
          >
            Reset to default
          </Typography>
        </Box>
      </Stack>
    </Modal>
  );
};

export default ColumnPicker;
